// EntityEngine.js
// Engine Pattern + SignalR push, op het platte Map-model van EntityCache (2026-09-16,
// zie EntityCache.js-comment voor waarom dit geen EAV/CacheBase2 meer is). Contract:
// 1 message per entity per batch, {rk, type, changes:[{name,value}]} — zie "EntityCard
// Data Message" in Jerny.Development voor de volledige onderbouwing.
import { events } from '@/services/wss/SystemEvents';
import ComponentNotifier from '../../../services/wss/ComponentNotifier';
import WssEventHandlerBase from '@/services/engines/WssEventHandlerBase';
import entityCache from './EntityCache';
import entityFetcher from './EntityFetcher';
import { P1_RK, P1_TYPE } from '../P1MeterWidget/p1MeterConst';

const _name = 'EntityEngine';

class EntityEngine extends WssEventHandlerBase {
    _name = _name;

    bootup() {
        super.bootup();
        this.clear();

        this._unsub = this._wsNotifier.subscribe(events.OnEntityDataChanged, this.onMessage);
        this._unsubPower = this._wsNotifier.subscribe(events.OnPowerUsageUpdated, this.onPowerUsage);
    }

    clear() {
        super.clear();
        if (this._unsub) this._unsub();
        if (this._unsubPower) this._unsubPower();
    }

    /**
     * HomeWizard P1 push (1/s) — written to the shared cache as entity 'p1'.
     * @param {{power: number, amp: number, ems: number}} data
     */
    onPowerUsage = ({ power, amp, ems }) => {
        try {
            entityCache.setRow(P1_RK, { type: P1_TYPE, power, amp }, ems);
            this._notify(P1_RK);
        } catch (error) {
            this.logError('onPowerUsage', error.message);
        }
    };

    /**
     * BE stuurt per entity 1 batch van gewijzigde velden — alles wat uit dezelfde
     * poll/tick van 1 service komt zit in 1 message, zodat de cache nooit een moment
     * met een halve/inconsistente update laat zien. `changes` wordt hier 1x omgezet
     * naar een plat object en in 1 setRow()-call weggeschreven — geen losse
     * cache-write per veld meer. Geen JSON.parse nodig, WssEventNotifier levert al
     * gedeserialiseerd.
     * `ems` zit op het envelope, niet in `changes` — dat is wanneer BE deze batch heeft
     * gemeten/verstuurd, en wordt 1-op-1 doorgezet naar EntityCache.setRow als "wanneer
     * hoorden we voor het laatst iets van deze entity" (zie EntityCache.js-comment).
     * @param {{rk: string, type: string, changes: {name: string, value: *}[], ems: number}} data
     */
    onMessage = ({ rk, type, changes, ems }) => {
        try {
            const fields = Object.fromEntries(changes.map(({ name, value }) => [name, value]));
            entityCache.setRow(rk, { type, ...fields }, ems);
            this._notify(rk);
        } catch (error) {
            this.logError('onMessage', error.message);
        }
    };

    /**
     * Haalt de daily cost/energy rows op (EntityFetcher) en schrijft elke row in 1
     * setRow()-call weg — zelfde shape als een SignalR-push, alleen al als plat object
     * i.p.v. een changes-array.
     * @param {string} [name] - entity name, default 'all' (prime every entity for today)
     * @param {number} [start] - timeframe start ems, 0 = today
     * @param {number} [end] - timeframe end ems, 0 = start + 1 day
     * @returns {Promise<object[]>} de rows zoals EntityFetcher ze teruggeeft
     */
    fetch = async (name = 'all', start = 0, end = 0) => {
        try {
            const rows = await entityFetcher.fetch(name, start, end);

            rows.forEach((row) => {
                const { rk, ems, ...fields } = row;
                entityCache.setRow(rk, fields, ems);
                this._notify(rk);
            });

            return rows;
        } catch (error) {
            this.logError('fetch', error.message);
            return [];
        }
    };

    /**
     * @param {string} rk
     * @returns {object|null} geassembleerde live (vandaag) row, of null als nog niks bekend is
     */
    getEntity = (rk) => entityCache.getRow(rk);

    /**
     * Cache-aside: geeft de cached row terug, of haalt bij een miss (of expliciete
     * refresh) de volledige state op via EntityFetcher.fetchCardState. Zelfde method
     * bedient dus zowel de eerste render (cache-miss) als een refresh-button
     * (force=true) — geen los refresh-pad nodig.
     * @param {string} rk - entity-identiteit, bv. 'tapo-01'
     * @param {boolean} [force] - sla cache-check over, altijd opnieuw fetchen
     * @returns {Promise<object|null>} de row na fetch, of de bestaande cached row
     */
    getCard = async (rk, force = false) => {
        const cached = entityCache.getRow(rk);
        if (cached && !force) return cached;

        const row = await entityFetcher.fetchCardState(rk);
        if (!row) return cached;

        const { ems, ...fields } = row;
        entityCache.setRow(rk, fields, ems);
        this._notify(rk);
        return entityCache.getRow(rk);
    };

    // notify draagt alleen de rk — component leest zelf terug uit de cache
    // (entityCache.getRow), nooit uit het notify-payload (zelfde reden als
    // Pnl3UnmatchedEngine._notify).
    _notify = (rk) => {
        ComponentNotifier.notify(_name, 'dataChanged', { rk });
    };
}

const entityEngine = new EntityEngine();
export default entityEngine;
